/**
 * Logbook Entry Table Component
 * 
 * Displays a table of logbook entries with all fields including Location & DX Info.
 */

import * as React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Loader2, Radio, Edit, Trash2, Eye } from "lucide-react";
import { useLocation } from "wouter";
import type { LogbookEntry } from "@/services/logbookService";
import { formatDateTime } from "../utils/blockUtils";
import { format } from "date-fns";

interface LogbookEntryTableProps {
  entries: LogbookEntry[];
  loading: boolean;
  error: string | null;
  onEdit?: (entry: LogbookEntry) => void;
  onDelete?: (entry: LogbookEntry) => void;
  onView?: (entry: LogbookEntry) => void;
}

export function LogbookEntryTable({
  entries,
  loading,
  error,
  onEdit,
  onDelete,
  onView,
}: LogbookEntryTableProps) {
  const [, setLocation] = useLocation();
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  const toggleRow = (entryId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(entryId)) {
      newExpanded.delete(entryId);
    } else {
      newExpanded.add(entryId);
    }
    setExpandedRows(newExpanded);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Logbook Entries</CardTitle>
          <CardDescription>
            Total: {entries.length} entr{entries.length !== 1 ? "ies" : "y"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Logbook Entries</CardTitle>
          <CardDescription>Error loading entries</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-destructive">
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Logbook Entries</CardTitle>
          <CardDescription>Total: 0 entries</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Radio className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No logbook entries found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Logbook Entries</CardTitle>
        <CardDescription>
          Total: {entries.length} entr{entries.length !== 1 ? "ies" : "y"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead className="min-w-[100px]">Date/Time</TableHead>
                <TableHead className="min-w-[120px]">Callsign</TableHead>
                <TableHead className="min-w-[80px]">Band</TableHead>
                <TableHead className="min-w-[80px]">Mode</TableHead>
                <TableHead className="min-w-[100px]">RST</TableHead>
                <TableHead className="min-w-[120px]">Location & DX Info</TableHead>
                <TableHead className="min-w-[100px]">QSL Status</TableHead>
                <TableHead className="min-w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => {
                const isExpanded = expandedRows.has(entry._id);
                const date = new Date(entry.date);
                const dateStr = format(date, "yyyy-MM-dd");
                const timeStr = entry.timeOn || "";

                return (
                  <React.Fragment key={entry._id}>
                    <TableRow className="cursor-pointer" onClick={() => toggleRow(entry._id)}>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(entry._id);
                          }}
                        >
                          {isExpanded ? "−" : "+"}
                        </Button>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-medium">{dateStr}</span>
                          <span className="text-xs text-muted-foreground">{timeStr}</span>
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="font-mono text-sm font-semibold">
                          {entry.callsign}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="text-sm">{entry.band}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="text-sm">{entry.mode}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-col gap-1 text-xs">
                          {entry.rstSent && <span>S: {entry.rstSent}</span>}
                          {entry.rstReceived && <span>R: {entry.rstReceived}</span>}
                          {!entry.rstSent && !entry.rstReceived && <span className="text-muted-foreground">-</span>}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex flex-col gap-1 text-xs">
                          {entry.gridSquare && (
                            <span className="font-mono">{entry.gridSquare}</span>
                          )}
                          {entry.country && (
                            <span className="text-muted-foreground">{entry.country}</span>
                          )}
                          {entry.dxcc && (
                            <span className="text-muted-foreground">DXCC: {entry.dxcc}</span>
                          )}
                          {!entry.gridSquare && !entry.country && !entry.dxcc && (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <span className="text-xs">{entry.qslStatus}</span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {onView && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                onView(entry);
                              }}
                            >
                              <Eye className="h-3 w-3" />
                            </Button>
                          )}
                          {onEdit ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEdit(entry);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLocation(`/logbook/${entry._id}/edit`);
                              }}
                            >
                              <Edit className="h-3 w-3" />
                            </Button>
                          )}
                          {onDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(entry);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={9} className="bg-muted/50">
                          <div className="p-4 space-y-4">
                            {/* Basic Info */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1">Frequency</p>
                                <p className="text-sm">{entry.frequency ? `${entry.frequency} kHz` : "-"}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1">Power</p>
                                <p className="text-sm">{entry.power ? `${entry.power}W` : "-"}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1">Antenna</p>
                                <p className="text-sm">{entry.antenna || "-"}</p>
                              </div>
                              <div>
                                <p className="text-xs font-semibold text-muted-foreground mb-1">Equipment</p>
                                <p className="text-sm">{entry.equipment || "-"}</p>
                              </div>
                            </div>

                            {/* Location & DX Info */}
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground mb-2">Location & DX Info</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Grid Square</p>
                                  <p className="text-sm font-mono">{entry.gridSquare || "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Country</p>
                                  <p className="text-sm">{entry.country || "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">State/Province</p>
                                  <p className="text-sm">{entry.state || "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">DXCC</p>
                                  <p className="text-sm">{entry.dxcc || "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">CQ Zone</p>
                                  <p className="text-sm">{entry.cqZone || "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">ITU Zone</p>
                                  <p className="text-sm">{entry.ituZone || "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Distance</p>
                                  <p className="text-sm">{entry.distance ? `${entry.distance} km` : "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Bearing</p>
                                  <p className="text-sm">{entry.bearing ? `${entry.bearing}°` : "-"}</p>
                                </div>
                              </div>
                            </div>

                            {/* QSL Info */}
                            <div>
                              <p className="text-xs font-semibold text-muted-foreground mb-2">QSL Information</p>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Status</p>
                                  <p className="text-sm">{entry.qslStatus}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Via</p>
                                  <p className="text-sm">{entry.qslVia || "-"}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Sent Date</p>
                                  <p className="text-sm">
                                    {entry.qslSentDate ? format(new Date(entry.qslSentDate), "yyyy-MM-dd") : "-"}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground mb-1">Received Date</p>
                                  <p className="text-sm">
                                    {entry.qslReceivedDate ? format(new Date(entry.qslReceivedDate), "yyyy-MM-dd") : "-"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Contest and Notes */}
                            {(entry.contestName || entry.notes) && (
                              <div>
                                {entry.contestName && (
                                  <div className="mb-2">
                                    <p className="text-xs font-semibold text-muted-foreground mb-1">Contest</p>
                                    <p className="text-sm">{entry.contestName}</p>
                                  </div>
                                )}
                                {entry.notes && (
                                  <div>
                                    <p className="text-xs font-semibold text-muted-foreground mb-1">Notes</p>
                                    <p className="text-sm whitespace-pre-wrap">{entry.notes}</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

